import {
  useFieldValue,
  useGetOne,
  useGetResourceLabel,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import { useParams } from 'react-router-dom';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { createBasicUserFilters } from '../User/UserFilters';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { UserPictureField } from '../User/UserPictureField';
import { StringArrayBadgeField } from '../User/StringArrayBadgeField';
import { ListExportButton } from '../../components/custom/ListExportButton';
import { createJourneyAnalyticsListExporter } from './createJourneyAnalyticsListExporter';
import { DataTable } from '../../components/admin/data-table';
import { NumberField } from '../../components/admin/number-field';
import { SelectInput } from '../../components/admin/select-input';
import { StatusBadge } from '../Moonstar/StatusBadge';
import { type FC, useMemo } from 'react';

const percentFormat = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 0,
}).format;

const JOURNEY_PROGRESS_CHOICES = [
  {
    value: 'not-started',
    label: 'moonstar.journeys.journeyProgress.notStarted',
  },
  {
    value: 'in-progress',
    label: 'moonstar.journeys.journeyProgress.inProgress',
  },
  {
    value: 'completed',
    label: 'moonstar.journeys.journeyProgress.completed',
  },
];

export const JourneyAnalytics: FC = () => {
  const { id } = useParams<{ id: string }>();
  const resource = useResourceContext();
  const journey = useGetOne(resource!, { id: id });
  const getLabel = useGetResourceLabel();
  const translate = useTranslate();
  const isSmallScreen = useIsSmallScreen();
  const userFilters = useMemo(
    () => createBasicUserFilters({ isSmallScreen }),
    [isSmallScreen]
  );

  return (
    <MoonstarList
      filters={[
        <SelectInput
          alwaysOn
          source="journeyProgress"
          optionText="label"
          optionValue="value"
          choices={JOURNEY_PROGRESS_CHOICES}
        />,
        ...userFilters,
      ]}
      filter={{
        userIsDisabled: false,
      }}
      actions={
        <MoonstarListActions
          hideCreate
          right={<ListExportButton maxResults={Infinity} />}
        />
      }
      sort={{ field: 'journeyProgressPercentage', order: 'DESC' }}
      resource={`journeys/${id}/analytics`}
      title={[
        getLabel(resource!, 1),
        journey.data ? journey.data.name : `# ${id}`,
        translate('moonstar.analytics.name'),
      ]}
      exporter={createJourneyAnalyticsListExporter({
        journeyName: journey.data ? journey.data.name : getLabel(resource!, 1),
      })}
    >
      <DataTable rowClick={false}>
        <DataTable.Col source="pictureUrl" disableSort>
          <UserPictureField />
        </DataTable.Col>
        <DataTable.Col
          source="internalId"
          label="resources.users.fields.internalId"
        />
        <DataTable.Col source="firstName" />
        <DataTable.Col source="lastName" />
        <DataTable.Col source="phoneNumber" />
        <DataTable.Col source="email" />
        <DataTable.Col source="jobTitleName" />
        <DataTable.Col source="departmentName" />
        <DataTable.Col source="regionName" />
        <DataTable.Col source="units" disableSort>
          <StringArrayBadgeField source="units" />
        </DataTable.Col>
        <DataTable.Col
          source="journeyProgressPercentage"
          label="resources.journeys.fields.journeyProgressPercentage"
        >
          <JourneyProgressField source="journeyProgressPercentage" />
        </DataTable.Col>
      </DataTable>
    </MoonstarList>
  );
};

const JourneyProgressField: FC<{ source: string }> = (props) => {
  const recordValue = useFieldValue(props);
  const value = Number(recordValue);

  if (recordValue === null || isNaN(value) || !isFinite(value)) {
    return <NumberField {...props} />;
  }

  return <JourneyProgressBadge value={value / 100} />;
};

const JourneyProgressBadge = ({ value }: { value: number }) => (
  <StatusBadge
    level={value >= 1 ? 'success' : value > 0 ? 'warning' : 'danger'}
  >
    {percentFormat(value)}
  </StatusBadge>
);
